import React from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { downloadReservationReport, getReservationReport } from '../api/reportsApi';
import { dateInputValue } from '../utils/date';

const REPORTS = {
  advance: {
    title: 'Advance Payment Report',
    subtitle: 'Advance-payment position by booking period and branch.'
  },
  covers: {
    title: 'Covers Report',
    subtitle: 'Reservation and same-day walk-in cover analysis.'
  },
  reconciliation: {
    title: 'Slot Cover Reconciliation',
    subtitle: 'Operational and financial reservation reconciliation.'
  },
  insight: {
    title: 'Reservation Insight',
    subtitle: 'Reservation lifecycle, on-floor timing, and no-show tracking.'
  },
  feedback: {
    title: 'Customer Feedback',
    subtitle: 'All reservations for the selected booking date, with feedback actions for eligible Served reservations.'
  }
};

const RESERVATION_STATUSES = ['Query', 'Reserved', 'Cancelled', 'Completed'];
const RESERVATION_STATES = ["Reservation's", 'On-Floor', 'Served', 'No-Shows'];

function todayKey() {
  return dateInputValue(new Date());
}

function monthStartKey() {
  const now = new Date();
  return dateInputValue(new Date(now.getFullYear(), now.getMonth(), 1));
}

function visibleBranches() {
  try {
    const branches = window.visibleBranchNames?.() || [];
    return Array.isArray(branches) && branches.length ? branches : ['Gulberg', 'Adda Plot'];
  } catch (err) {
    return ['Gulberg', 'Adda Plot'];
  }
}

function metric(value) {
  const number = Number(value || 0);
  return Number.isInteger(number) ? number.toLocaleString() : number.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function reportDate(value) {
  if (!value) return '-';
  const key = String(value).slice(0, 10);
  const date = new Date(`${key}T00:00:00`);
  return Number.isNaN(date.getTime()) ? key : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function reportDateTime(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value || '-') : date.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function statusClass(value = '') {
  const key = String(value).toLowerCase();
  if (key === 'reserved' || key === 'paid' || key === 'completed') return 'report-status-positive';
  if (key === 'cancelled') return 'report-status-negative';
  if (key.includes('arrival')) return 'report-status-orange';
  if (key.includes('waived')) return 'report-status-purple';
  return 'report-status-neutral';
}

function StatusPill({ value }) {
  return <span className={`report-status ${statusClass(value)}`}>{value || '-'}</span>;
}

function SummaryCard({ label, value, sub, color = 'var(--text)' }) {
  return (
    <div className="slot-insight-card report-summary-card" style={{ '--accent-color': color }}>
      <div className="slot-insight-label">{label}</div>
      <div className="slot-insight-value">{metric(value)}</div>
      {sub ? <div className="slot-insight-sub">{sub}</div> : null}
    </div>
  );
}

function AdvanceSummary({ summary = {} }) {
  return (
    <div className="report-summary-grid">
      <SummaryCard label="Total Reservations" value={summary.totalReservations} sub={`${metric(summary.totalPax)} PAX`} />
      <SummaryCard label="Advance Paid" value={summary.advancePaid} sub={`${metric(summary.advancePaidPax)} PAX · Rs ${metric(summary.advancePaidReceivedAmount)} received`} color="var(--green)" />
      <SummaryCard label="Advance Waived" value={summary.advanceWaived} sub={`${metric(summary.advanceWaivedPax)} PAX`} color="var(--purple)" />
      <SummaryCard label="Pay on Arrival" value={summary.payOnArrival} sub={`${metric(summary.payOnArrivalPax)} PAX`} color="var(--orange)" />
      <SummaryCard label="Advance Pending" value={summary.advancePending} sub={`${metric(summary.advancePendingPax)} PAX`} color="var(--text3)" />
    </div>
  );
}

function CoversSummary({ summary = {} }) {
  const total = Number(summary.totalCovers || 0);
  const reservation = Number(summary.reservationCovers || 0);
  const walkIn = Number(summary.walkInCovers || 0);
  return (
    <div className="report-summary-grid report-summary-grid-covers">
      <SummaryCard label="Total Covers" value={total} sub={`${metric(summary.totalReservations)} reservations`} />
      <SummaryCard label="Reservation Covers" value={reservation} sub={`${metric(summary.reservationBookings)} reservations · ${total ? Math.round((reservation / total) * 100) : 0}% of covers`} color="var(--blue)" />
      <SummaryCard label="Walk-in Covers" value={walkIn} sub={`${metric(summary.walkInReservations)} reservations · ${total ? Math.round((walkIn / total) * 100) : 0}% of covers`} color="var(--green)" />
      <div className="report-percentage-card">
        <div className="report-percentage-bar"><span style={{ width: `${total ? (reservation / total) * 100 : 0}%` }} /><i style={{ width: `${total ? (walkIn / total) * 100 : 0}%` }} /></div>
        <div><span>Reservation {total ? Math.round((reservation / total) * 100) : 0}%</span><span>Walk-in {total ? Math.round((walkIn / total) * 100) : 0}%</span></div>
      </div>
    </div>
  );
}

function StatusMultiSelect({ values, onChange, options = RESERVATION_STATUSES, allLabel = 'All Statuses', countLabel = 'statuses' }) {
  const toggle = value => onChange(values.includes(value) ? values.filter(item => item !== value) : [...values, value]);
  return (
    <details className="accounts-multi-select report-status-select">
      <summary className="accounts-multi-select-trigger">{values.length ? `${values.length} ${countLabel}` : allLabel}</summary>
      <div className="accounts-multi-select-menu">
        {options.map(status => <label key={status} className="accounts-multi-select-option"><input type="checkbox" checked={values.includes(status)} onChange={() => toggle(status)} />{status}</label>)}
      </div>
    </details>
  );
}

function columnsFor(type) {
  if (type === 'feedback') return [
    ['reservationId', 'Reservation ID'], ['name', 'Name'], ['number', 'Phone Number'], ['branch', 'Branch'], ['slot', 'Slot'],
    ['createdAt', 'Created at'], ['bookingDate', 'Booking date'], ['onFloor', 'On-floor'], ['served', 'Served'],
    ['totalOnFloorTime', 'Total On-Floor Time'], ['markedNoShowAt', 'Marked No-Show at'],
    ['experienceRating', 'How was your experience?'], ['foodQualityRating', 'How was food Quality?'],
    ['visitAgain', 'Would you like to visit again?'], ['suggestions', 'Any suggestions?'],
    ['feedbackSource', 'Feedback Source'], ['submittedBy', 'Submitted By'], ['csrAccount', 'CSR Account'],
    ['agentAlias', 'Agent Alias'], ['submittedAt', 'Submitted At'], ['feedbackAction', 'Feedback']
  ];
  if (type === 'insight') return [
    ['reservationId', 'Reservation ID'], ['name', 'Name'], ['branch', 'Branch'], ['slot', 'Slot'],
    ['createdAt', 'Created at'], ['bookingDate', 'Booking date'], ['onFloor', 'On-floor'],
    ['served', 'Served'], ['totalOnFloorTime', 'Total On-Floor Time'], ['markedNoShowAt', 'Marked No-Show at']
  ];
  const columns = [
    ['reservationId', 'Reservation ID'],
    ['bookingDate', 'Booking Date'],
    ['createdDate', 'Created Date'],
    ['name', 'Guest Name'],
    ['number', 'Contact Number'],
    ['branch', 'Branch'],
    ['slot', 'Slot'],
    ['advanceStatus', 'Advance Status'],
    ['reservationStatus', 'Reservation Status'],
    ['advanceAmount', 'Advance Amount']
  ];
  if (type === 'covers') columns.push(['coverType', 'Cover Type'], ['covers', 'Covers']);
  if (type === 'reconciliation') columns.push(['invoiceNo', 'Invoice Number'], ['billingPax', 'Billing PAX'], ['reservationState', 'Reservation ID State']);
  return columns;
}

function defaultColumnKeys(type) {
  return columnsFor(type).map(([key]) => key);
}

function ReportCell({ column, record, type, canSubmitFeedback = false }) {
  if (type === 'feedback' && column === 'feedbackAction') {
    if (record.feedbackSubmitted) return <button type="button" className="btn-secondary" onClick={() => window.openFeedbackModal?.(encodeURIComponent(record.recordId || ''))}>View Feedback</button>;
    if (record.feedbackEligible && canSubmitFeedback) return <button type="button" className="btn-primary" onClick={() => window.openFeedbackModal?.(encodeURIComponent(record.recordId || ''))}>Feedback</button>;
    return <StatusPill value={record.feedbackEligible ? 'Pending' : 'Not Eligible'} />;
  }
  if (type === 'feedback' && ['experienceRating', 'foodQualityRating'].includes(column)) {
    const rating = Number(record[column] || 0);
    return rating ? <span title={`${rating} out of 5`} style={{ color:'#f5a623', whiteSpace:'nowrap', fontSize:16 }}>{'★'.repeat(rating)}{'☆'.repeat(5 - rating)}</span> : '-';
  }
  if (type === 'feedback' && column === 'submittedAt') return record.submittedAt ? reportDateTime(record.submittedAt) : '-';
  if (type === 'feedback' && ['visitAgain', 'feedbackSource'].includes(column)) return record[column] ? <StatusPill value={record[column]} /> : '-';
  if (type === 'insight' || type === 'feedback') return record[column] || '-';
  if (['createdAt', 'onFloor', 'served', 'markedNoShowAt'].includes(column)) return record[column] ? reportDateTime(record[column]) : '-';
  if (column === 'totalOnFloorTime') return record.totalOnFloorTime || '0m';
  if (column === 'bookingDate') return reportDate(record.bookingDateKey || record.bookingDate);
  if (column === 'createdDate') return reportDate(record.createdDateKey || record.createdDate);
  if (column === 'advanceStatus' || column === 'reservationStatus' || column === 'reservationState' || column === 'coverType') return <StatusPill value={record[column]} />;
  if (column === 'advanceAmount') return `Rs ${Number(record.advanceAmount || 0).toLocaleString()}`;
  if (column === 'covers' || column === 'billingPax') return metric(record[column]);
  return record[column] || '-';
}

export function ReportsPage({ type }) {
  const definition = REPORTS[type] || REPORTS.reconciliation;
  const [draft, setDraft] = React.useState({ start: monthStartKey(), end: todayKey(), branch: '', statuses: [], states: [], search: '' });
  const [applied, setApplied] = React.useState({ ...draft });
  const [page, setPage] = React.useState(1);
  const [sortBy, setSortBy] = React.useState(type === 'feedback' ? 'submittedAt' : 'bookingDate');
  const [sortDir, setSortDir] = React.useState('desc');
  const [exporting, setExporting] = React.useState(false);
  const [minimumFloorMinutes, setMinimumFloorMinutes] = React.useState(30);
  const [visibleColumnsByType, setVisibleColumnsByType] = React.useState(() => ({
    advance: defaultColumnKeys('advance'),
    covers: defaultColumnKeys('covers'),
    reconciliation: defaultColumnKeys('reconciliation'),
    insight: defaultColumnKeys('insight'),
    feedback: defaultColumnKeys('feedback')
  }));
  const params = React.useMemo(() => ({
    start: applied.start,
    end: applied.end,
    branch: applied.branch,
    statuses: type === 'reconciliation' ? applied.statuses : [],
    states: type === 'reconciliation' || type === 'insight' ? applied.states : [],
    search: applied.search,
    page,
    limit: 50,
    sortBy,
    sortDir,
    minimumFloorMinutes: type === 'insight' ? minimumFloorMinutes : undefined
  }), [applied, page, sortBy, sortDir, type, minimumFloorMinutes]);
  const query = useQuery({
    queryKey: ['reports', type, params],
    queryFn: () => getReservationReport(type, params),
    placeholderData: previous => previous
  });
  React.useEffect(() => {
    if (type !== 'feedback') return undefined;
    const refresh = () => query.refetch();
    window.addEventListener('customer-feedback-updated', refresh);
    return () => window.removeEventListener('customer-feedback-updated', refresh);
  }, [type, query.refetch]);
  const availableColumns = React.useMemo(() => columnsFor(type), [type]);
  const visibleColumnKeys = visibleColumnsByType[type] || defaultColumnKeys(type);
  const columns = availableColumns.filter(([key]) => visibleColumnKeys.includes(key));
  const toggleColumn = key => {
    setVisibleColumnsByType(value => {
      const current = value[type] || defaultColumnKeys(type);
      const next = current.includes(key) ? current.filter(item => item !== key) : [...current, key];
      return { ...value, [type]: next.length ? next : current };
    });
  };
  const apply = () => {
    setApplied({ ...draft });
    setPage(1);
  };
  const sort = column => {
    if (!['reservationId', 'bookingDate', 'createdDate', 'createdAt', 'name', 'branch', 'slot', 'advanceStatus', 'reservationStatus', 'advanceAmount', 'invoiceNo', 'billingPax', 'reservationState', 'onFloor', 'served', 'markedNoShowAt', 'totalOnFloorTime', 'submittedAt'].includes(column)) return;
    if (sortBy === column) setSortDir(value => value === 'asc' ? 'desc' : 'asc');
    else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(1);
  };
  const exportReport = async format => {
    setExporting(true);
    try {
      await downloadReservationReport(type, { ...params, page: undefined, limit: undefined }, format);
    } catch (err) {
      window.toast?.(err.message || 'Report export failed', 'red');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="page-header reports-page-head">
        <div><h1>{definition.title}</h1><p>{definition.subtitle}</p></div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {type === 'insight' ? <div className="filter-group"><span className="filter-label">Minimum On-Floor</span><select value={minimumFloorMinutes} onChange={event => { setMinimumFloorMinutes(Number(event.target.value)); setPage(1); }}><option value="30">30 minutes</option><option value="45">45 minutes</option><option value="60">60 minutes</option></select></div> : null}
          <button type="button" className="btn-secondary" onClick={() => exportReport('csv')} disabled={exporting}>{exporting ? 'Preparing...' : 'CSV'}</button>
          {type === 'insight' ? <><button type="button" className="btn-secondary" onClick={() => exportReport('xlsx')} disabled={exporting}>XLSX</button><button type="button" className="btn-secondary" onClick={() => exportReport('pdf')} disabled={exporting}>PDF</button></> : null}
        </div>
      </div>
      <div className="filters-bar reports-filters">
        <div className="filter-group"><span className="filter-label">From</span><input type="date" value={draft.start} onChange={event => setDraft(value => ({ ...value, start: event.target.value }))} /></div>
        <div className="filter-group"><span className="filter-label">To</span><input type="date" value={draft.end} onChange={event => setDraft(value => ({ ...value, end: event.target.value }))} /></div>
        <div className="filter-group"><span className="filter-label">Branch</span><select value={draft.branch} onChange={event => setDraft(value => ({ ...value, branch: event.target.value }))}><option value="">All Branches</option>{visibleBranches().map(branch => <option key={branch} value={branch}>{branch}</option>)}</select></div>
        {type === 'reconciliation' ? <div className="filter-group"><span className="filter-label">Reservation Status</span><StatusMultiSelect values={draft.statuses} onChange={statuses => setDraft(value => ({ ...value, statuses }))} /></div> : null}
        {type === 'reconciliation' || type === 'insight' ? <div className="filter-group"><span className="filter-label">{type === 'insight' ? 'Reservation State' : 'Reservation ID State'}</span><StatusMultiSelect values={draft.states} onChange={states => setDraft(value => ({ ...value, states }))} options={RESERVATION_STATES} allLabel="All States" countLabel="states" /></div> : null}
        <button type="button" className="btn-primary reports-apply" onClick={apply}>Apply</button>
      </div>
      <div className="accounts-search-row reports-search-row">
        <input className="search-bar" placeholder="Search reservation ID, guest, phone, branch, or slot..." value={draft.search} onChange={event => setDraft(value => ({ ...value, search: event.target.value }))} onKeyDown={event => { if (event.key === 'Enter') apply(); }} />
        <button type="button" className="btn-secondary" onClick={apply}>Search</button>
      </div>
      {type === 'advance' ? <AdvanceSummary summary={query.data?.summary} /> : null}
      {type === 'covers' ? <CoversSummary summary={query.data?.summary} /> : null}
      {type === 'reconciliation' ? <div className="report-summary-grid"><SummaryCard label="Matching Reservations" value={query.data?.total} /></div> : null}
      {query.error ? <div className="empty-state"><p>{query.error.message || 'Report failed to load.'}</p></div> : null}
      <div className="chart-card reports-table-card" style={{ opacity: query.isFetching && !query.isLoading ? 0.85 : 1 }}>
        <div className="chart-title reports-table-title">
          <span>Report Details <small>{query.isLoading ? 'Loading...' : `${Number(query.data?.total || 0).toLocaleString()} records`}</small></span>
          <details className="report-column-picker">
            <summary title="Select columns">⚙</summary>
            <div className="report-column-menu">
              <strong>Columns</strong>
              {availableColumns.map(([key, label]) => (
                <label key={key}><input type="checkbox" checked={visibleColumnKeys.includes(key)} onChange={() => toggleColumn(key)} />{label}</label>
              ))}
            </div>
          </details>
        </div>
        <div className="table-wrap reports-table-wrap">
          <table className="reports-table">
            <thead><tr><th>Sr No.</th>{columns.map(([key, label]) => <th key={key} className="report-sortable" onClick={() => sort(key)}>{label}{sortBy === key ? ` ${sortDir === 'asc' ? '↑' : '↓'}` : ''}</th>)}</tr></thead>
            <tbody>
              {(query.data?.records || []).map((record, index) => <tr key={record.recordId || `${record.reservationId}-${index}`} className={type === 'insight' && record.belowMinimum ? 'report-row-below-minimum' : ''}><td className="td-mono">{((page - 1) * 50) + index + 1}</td>{columns.map(([key]) => <td key={key} className={['reservationId', 'number', 'invoiceNo'].includes(key) ? 'td-mono' : ''}><ReportCell column={key} record={record} type={type} canSubmitFeedback={Boolean(query.data?.canSubmitFeedback)} /></td>)}</tr>)}
              {!query.isLoading && !(query.data?.records || []).length ? <tr><td colSpan={columns.length + 1}><div className="empty-state"><p>No records match these filters.</p></div></td></tr> : null}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span className="page-info">Page {query.data?.page || 1} of {query.data?.totalPages || 1}</span>
          <button className="page-btn" disabled={page <= 1 || query.isFetching} onClick={() => setPage(value => Math.max(1, value - 1))}>Previous</button>
          <button className="page-btn" disabled={page >= Number(query.data?.totalPages || 1) || query.isFetching} onClick={() => setPage(value => value + 1)}>Next</button>
        </div>
      </div>
    </>
  );
}

export function ReportsPageMount({ type }) {
  const [target, setTarget] = React.useState(null);
  React.useEffect(() => setTarget(document.getElementById(`react-report-${type}`)), [type]);
  if (!target) return null;
  return createPortal(<ReportsPage type={type} />, target);
}
